import { api } from '../api/client'

/** One person's workspace documents, kept on the server (db/035,
 *  services/workspace_store.py): the analysis library, saved comparison
 *  views, map symbols and sensor selection, the overview layout. Workspace
 *  v5 kept each as one JSON string under one browser storage key, and its
 *  modules read and write them through a `Storage`-shaped object
 *  (`readAnalyses(storage?)`, `writeMapSymbols(…, storage?)`). This is that
 *  object, backed by the server, so those modules change only their default:
 *
 *  - `loadDocuments()` fetches every document once after sign-in
 *    (`WorkspaceReady` waits for it), and `getItem` answers from what came.
 *  - `setItem` keeps the new text at once and saves it a moment later, the
 *    last change per key winning; leaving or hiding the page sends what is
 *    waiting (as a keepalive request, which outlives the page), and so does
 *    signing out, before the session ends.
 *  - Until the documents have loaded, or if loading failed, `setItem`
 *    throws, which every caller reports as "could not save": writing before
 *    the person's own documents are known would overwrite them.
 *
 *  The documents are the person's alone (the server keys them on the
 *  session) and every module checks a document as it reads it, as before. */

/** The browser storage keys the fork used, and the server's names for them. */
export const DOCUMENT_KEYS: Record<string, string> = {
  'hydro.analysis-library.v1': 'analyses',
  'hydro:comparison-views:v1': 'comparison-views',
  'hydro:map-symbols:v1': 'map-symbols',
  'hydro:map-sensor-selection:v1': 'map-sensor-selection',
  'hydro.overview-layout.v1': 'overview-layout',
  'hydro.hidden-channels.v1': 'hidden-channels',
}
export const DOCUMENTS_FAILED_EVENT = 'hydro:documents-failed'
const SAVE_DELAY_MS = 600

type Status = 'idle' | 'loading' | 'ready' | 'failed'
let status: Status = 'idle'
let held = new Map<string, unknown>()
let loading: Promise<void> | null = null
// Whose documents: signing out starts a new era, and an answer still on
// its way for the person before is dropped.
let era = 0
const waiting = new Map<string, ReturnType<typeof setTimeout>>()
const listeners = new Set<() => void>()
let flushing = false

function serverKey(key: string): string {
  const name = DOCUMENT_KEYS[key]
  if (!name) throw new Error(`No workspace document is kept under ${key}.`)
  return name
}

function announce(): void { listeners.forEach(listener => listener()) }

export function documentsStatus(): Status { return status }
export function subscribeDocuments(listener: () => void): () => void {
  listeners.add(listener)
  return () => { listeners.delete(listener) }
}

/** Every document of the person signed in, once; again after `forgetDocuments`. */
export function loadDocuments(): Promise<void> {
  if (status === 'ready') return Promise.resolve()
  if (loading) return loading
  // From first use, never on import: importing a module must not touch the window.
  if (!flushing && typeof window !== 'undefined' && typeof window.addEventListener === 'function') {
    window.addEventListener('pagehide', () => { void flushDocuments(true) })
    if (typeof document !== 'undefined') {
      document.addEventListener('visibilitychange', () => { if (document.visibilityState === 'hidden') void flushDocuments(true) })
    }
    flushing = true
  }
  status = 'loading'; announce()
  const ours = era
  const request: Promise<void> = api.documents.all().then(({ documents }) => {
    if (ours !== era) return
    held = new Map(Object.entries(documents))
    status = 'ready'
  }, () => { if (ours === era) status = 'failed' }).finally(() => {
    if (loading === request) loading = null
    if (ours === era) announce()
  })
  loading = request
  return request
}

/** Signing out: nothing of one person's may show for the next. */
export function forgetDocuments(): void {
  era++
  waiting.forEach(timer => clearTimeout(timer))
  waiting.clear(); held = new Map(); status = 'idle'; loading = null
}

async function save(name: string, keepalive = false): Promise<void> {
  waiting.delete(name)
  try {
    if (held.has(name)) await api.documents.put(name, held.get(name), { keepalive })
    else await api.documents.remove(name, { keepalive })
  } catch (cause) {
    if (typeof window !== 'undefined') {
      window.dispatchEvent(new CustomEvent(DOCUMENTS_FAILED_EVENT, {
        detail: cause instanceof Error ? cause.message : 'Your workspace settings could not be saved.' }))
    }
  }
}

function schedule(name: string): void {
  const prior = waiting.get(name)
  if (prior) clearTimeout(prior)
  waiting.set(name, setTimeout(() => { void save(name) }, SAVE_DELAY_MS))
}

/** What is waiting, sent now: the page is going (`keepalive`), or the
 *  person is signing out, who waits for it. */
export function flushDocuments(keepalive = false): Promise<void> {
  const names = [...waiting.keys()]
  for (const name of names) clearTimeout(waiting.get(name)!)
  return Promise.all(names.map(name => save(name, keepalive))).then(() => undefined)
}

/** The `Storage` the workspace modules read and write by default. */
export const serverStorage: Pick<Storage, 'getItem' | 'setItem' | 'removeItem'> = {
  getItem(key: string): string | null {
    const name = serverKey(key)
    return held.has(name) ? JSON.stringify(held.get(name)) : null
  },
  setItem(key: string, value: string): void {
    const name = serverKey(key)
    if (status !== 'ready') throw new Error('Your workspace settings have not loaded, so nothing is saved.')
    held.set(name, JSON.parse(value))
    schedule(name)
  },
  removeItem(key: string): void {
    const name = serverKey(key)
    if (status !== 'ready') throw new Error('Your workspace settings have not loaded, so nothing is saved.')
    held.delete(name)
    schedule(name)
  },
}
